import { getDocumentReaderRunResultsUrl } from '@commenda/api/commenda-ai/document-reader/getDocumentReaderRunResultsUrl';
import { Button, Progress } from '@commenda/kakashi';
import {
  AlertCircleIcon,
  ArchiveIcon,
  CheckCircle2Icon,
  CheckIcon,
  ExternalLinkIcon,
  Loader2Icon,
} from 'lucide-react';

import {
  DocumentReadingRunTone,
  DocumentReadingStepStatus,
} from './DocumentReadingRunHelper';
import { useDocumentReadingRunCard } from './useDocumentReadingRunCard';

interface DocumentReadingRunCardProps {
  runId: string;
}

const CARD_CLASS_NAMES =
  'flex w-full flex-col gap-lg rounded-lg border-[0.5px] border-border-primary bg-bg-primary p-xl shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)]';

export function DocumentReadingRunCard({
  runId,
}: Readonly<DocumentReadingRunCardProps>) {
  const { cardView, isLoading, error, isApproving, approveError, approveRun } =
    useDocumentReadingRunCard(runId);

  if (!cardView)
    return (
      <div className={CARD_CLASS_NAMES}>
        <div className="flex items-center gap-md">
          {isLoading ? (
            <Loader2Icon className="size-4 shrink-0 animate-spin text-fg-tertiary" />
          ) : (
            <AlertCircleIcon className="size-4 shrink-0 text-text-error" />
          )}
          <p className="txt-small-regular text-text-secondary">
            {error && !isLoading
              ? 'Could not load the reading status. Ask the chat for it instead.'
              : 'Loading the reading status'}
          </p>
        </div>
      </div>
    );

  return (
    <div className={CARD_CLASS_NAMES}>
      <div className="flex items-center gap-md">
        {cardView.tone === DocumentReadingRunTone.WORKING && (
          <Loader2Icon className="size-4 shrink-0 animate-spin text-fg-tertiary" />
        )}
        {cardView.tone === DocumentReadingRunTone.WAITING && (
          <ArchiveIcon className="size-4 shrink-0 text-fg-secondary" />
        )}
        {cardView.tone === DocumentReadingRunTone.DONE && (
          <CheckCircle2Icon className="size-4 shrink-0 text-fg-success" />
        )}
        {cardView.tone === DocumentReadingRunTone.PROBLEM && (
          <AlertCircleIcon className="size-4 shrink-0 text-text-error" />
        )}
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="txt-small-medium text-text-primary">
            {cardView.headline}
          </span>
          <span className="txt-extra-small-regular text-text-tertiary">
            Document reader
          </span>
        </div>
        <span className="txt-extra-small-medium shrink-0 tabular-nums text-text-secondary">
          {cardView.percent}%
        </span>
      </div>

      <Progress
        accent={
          cardView.tone === DocumentReadingRunTone.DONE
            ? 'success'
            : cardView.tone === DocumentReadingRunTone.PROBLEM
              ? 'error'
              : 'blue'
        }
        value={cardView.percent}
        className="rounded-full"
      />

      <ol className="flex flex-wrap gap-xs">
        {cardView.steps.map((step) => (
          <li
            key={step.label}
            className={
              step.status === DocumentReadingStepStatus.CURRENT
                ? 'txt-extra-small-medium flex items-center gap-xxs rounded-md bg-bg-secondary px-sm py-xxs text-text-primary'
                : step.status === DocumentReadingStepStatus.DONE
                  ? 'txt-extra-small-regular flex items-center gap-xxs px-sm py-xxs text-text-secondary'
                  : 'txt-extra-small-regular flex items-center gap-xxs px-sm py-xxs text-text-tertiary'
            }
          >
            {step.status === DocumentReadingStepStatus.DONE && (
              <CheckIcon className="size-3 text-fg-success" />
            )}
            {step.label}
          </li>
        ))}
      </ol>

      {(cardView.readingLine ||
        cardView.modelCallLine ||
        cardView.filesLine ||
        cardView.spendLine ||
        cardView.outcomeLine) && (
        <div className="flex flex-col gap-xxs">
          {cardView.readingLine && (
            <p className="txt-small-regular text-text-primary">
              {cardView.readingLine}
            </p>
          )}
          {cardView.modelCallLine && (
            <p className="txt-small-regular tabular-nums text-text-primary">
              {cardView.modelCallLine}
            </p>
          )}
          {cardView.outcomeLine && (
            <p className="txt-small-regular text-text-primary">
              {cardView.outcomeLine}
            </p>
          )}
          {cardView.filesLine && (
            <p className="txt-extra-small-regular text-text-secondary">
              {cardView.filesLine}
            </p>
          )}
          {cardView.spendLine && (
            <p className="txt-extra-small-regular tabular-nums text-text-secondary">
              {cardView.spendLine}
            </p>
          )}
        </div>
      )}

      {cardView.isAwaitingApproval && (
        <div className="flex flex-wrap items-center gap-md rounded-md bg-bg-secondary p-md">
          <p className="txt-small-regular min-w-0 flex-1 text-text-primary">
            {cardView.estimateLine}
          </p>
          <Button
            variant="primary"
            size="xs"
            type="button"
            disabled={isApproving}
            onClick={approveRun}
          >
            {isApproving ? 'Starting' : 'Approve and start reading'}
          </Button>
        </div>
      )}

      {Boolean(error) && (
        <p className="txt-extra-small-regular text-text-error">
          Could not refresh the reading status, so these numbers may be out of
          date.
        </p>
      )}

      {cardView.isAwaitingApproval && approveError && (
        <p className="txt-extra-small-regular text-text-error">
          {approveError}
        </p>
      )}

      {cardView.isCompleted && (
        <a
          href={getDocumentReaderRunResultsUrl(runId)}
          target="_blank"
          rel="noreferrer"
          className="txt-small-medium flex w-fit items-center gap-xs text-text-brand-primary"
        >
          View results
          <ExternalLinkIcon className="size-4" />
        </a>
      )}
    </div>
  );
}
